import { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/forside')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then((data) => {
        setPageData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching homepage content from CMS:', err);
        // Fallback to seeded content if API is unreachable
        setPageData({
          title: 'Forside',
          meta_title: 'Dansketelemaster Invest',
          meta_description: 'Dansketelemaster Invest i Aalborg Ø. Find kontaktoplysninger, adresse, telefon, e-mail, CVR og virksomhedsoplysninger.',
          content_html: '<h2>Lån 10 gange din årlige lejeindtægt GRATIS i 10 år</h2><p>Ring 50 358 378 eller send os en mail på info@dansketelemasterinvest.dk</p>',
        });
        setLoading(false);
      });
  }, []);

  const metaTitle = pageData?.meta_title || 'Dansketelemaster Invest';
  const metaDescription = pageData?.meta_description || 'Dansketelemaster Invest i Aalborg Ø. Find kontaktoplysninger, adresse, telefon, e-mail, CVR og virksomhedsoplysninger.';

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SEO title={metaTitle} description={metaDescription} />

      {/* Hero Section */}
      <section 
        className="relative bg-slate-900 border-b border-slate-100 overflow-hidden bg-cover bg-top py-20 md:py-32 lg:py-40"
        style={{
          backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/1a874191-82d1-41a7-bad4-47bc2252f075/imgi-10-design-uden-navn-8.jpg')`
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-slate-950/75 mix-blend-multiply pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2a4692]/90 to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl space-y-8 text-left">
            {/* Skip to content utility link */}
            <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-white focus:p-4 focus:text-[#2a4692] focus:font-semibold focus:border focus:border-slate-200 focus:rounded-md mb-4 inline-block">
              Skip to content
            </a>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
              Lån 10 gange din årlige lejeindtægt <span className="text-blue-400">GRATIS</span> i 10 år
            </h1>

            <div className="pt-2">
              <a
                href="#main-content"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('main-content')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white hover:bg-blue-50 text-[#2a4692] font-bold px-8 py-4 rounded-md transition-all inline-flex items-center gap-3 shadow-md hover:shadow-lg cursor-pointer text-base"
              >
                Kontakt
                <i className="fa-solid fa-arrow-right text-sm" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Details & Form Section */}
      <section id="main-content" className="py-16 px-6 bg-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xl sm:text-3xl font-extrabold text-slate-900 tracking-tight break-words">
              Kontakt os
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              Dansketelemaster Invest er din pålidelige partner inden for kapitalfrigørelse af telemaster. Send os en besked via formularen, eller find vores virksomhedsoplysninger nedenfor.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Column: Virksomhedsoplysninger */}
            <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200/60 p-6 md:p-8 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-6 pb-3 border-b border-slate-100 flex items-center gap-2.5">
                <i className="fa-solid fa-circle-info text-[#2a4692]" />
                Virksomhedsoplysninger
              </h3>
              <ul className="divide-y divide-slate-100">
                {/* Adresse */}
                <li className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#2a4692] shrink-0">
                      <i className="fa-solid fa-location-dot text-lg" />
                    </div>
                    <span className="font-bold text-slate-700 text-base">Adresse</span>
                  </div>
                  <a 
                    href="https://maps.app.goo.gl/uK69D1PZc6Z87X6u9" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-slate-800 font-semibold hover:text-[#2a4692] transition-colors hover:underline text-left sm:text-right"
                  >
                    Niels Jernes Vej 10, 9220 Aalborg Ø
                  </a>
                </li>

                {/* Telefon */}
                <li className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#2a4692] shrink-0">
                      <i className="fa-solid fa-phone text-lg" />
                    </div>
                    <span className="font-bold text-slate-700 text-base">Telefon</span>
                  </div>
                  <a 
                    href="tel:+4550358378" 
                    className="text-slate-800 font-semibold hover:text-[#2a4692] transition-colors text-lg whitespace-nowrap text-left sm:text-right"
                  >
                    50 358 378
                  </a>
                </li>

                {/* E-mail */}
                <li className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#2a4692] shrink-0">
                      <i className="fa-solid fa-envelope text-lg" />
                    </div>
                    <span className="font-bold text-slate-700 text-base">E-mail</span>
                  </div>
                  <a 
                    href="mailto:info@dansketelemasterinvest.dk" 
                    className="text-slate-800 font-semibold hover:text-[#2a4692] transition-colors whitespace-nowrap text-left sm:text-right"
                  >
                    info@dansketelemasterinvest.dk
                  </a>
                </li>

                {/* CVR */}
                <li className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#2a4692] shrink-0">
                      <i className="fa-solid fa-id-card text-lg" />
                    </div>
                    <span className="font-bold text-slate-700 text-base">CVR-nummer</span>
                  </div>
                  <span className="text-slate-800 font-bold text-lg text-left sm:text-right">
                    21810797
                  </span>
                </li>

                {/* Bank */}
                <li className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#2a4692] shrink-0">
                      <i className="fa-solid fa-building-columns text-lg" />
                    </div>
                    <span className="font-bold text-slate-700 text-base">Bankforbindelse</span>
                  </div>
                  <span className="text-slate-800 font-semibold text-left sm:text-right">
                    Danske Bank
                  </span>
                </li>
              </ul>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <div className="w-full overflow-hidden border-b border-slate-100">
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2137.402482322345!2d9.9793133!3d57.0148533!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4649326770f5e399%3A0x6a9336b13994c92!2sNiels%20Jernes%20Vej%2010%2C%209220%20Aalborg%20%C3%98!5e0!3m2!1sda!2sdk!4v1719234903149!5m2!1sda!2sdk" 
          className="w-full h-[250px] md:h-[400px] border-0 grayscale hover:grayscale-0 transition-all duration-700 ease-in-out"
          allowFullScreen="" 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      {/* Opening Hours Section */}
      <section className="py-16 px-6 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-[#2a4692] mb-4">
              <i className="fa-solid fa-clock text-xl" />
            </div>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Åbningstider
            </h2>
            <p className="mt-2 text-slate-500">
              Vi sidder klar til at besvare dine henvendelser i følgende tidsrum
            </p>
          </div>

          <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 md:p-8 shadow-xs max-w-2xl mx-auto">
            <div className="space-y-3.5">
              {[
                { day: 'Mandag', hours: '10:00 – 17:00' },
                { day: 'Tirsdag', hours: '10:00 – 17:00' },
                { day: 'Onsdag', hours: '10:00 – 17:00' },
                { day: 'Torsdag', hours: '10:00 – 20:00' },
                { day: 'Fredag', hours: '10:00 – 15:00' },
                { day: 'Lørdag', hours: '10:00 – 14:00' },
                { day: 'Søndag', hours: 'Lukket', closed: true },
              ].map((item) => (
                <div 
                  key={item.day} 
                  className="flex justify-between items-center border-b border-slate-200/60 pb-3 last:border-0 last:pb-0"
                >
                  <span className="font-bold text-slate-700 text-base">{item.day}</span>
                  <span className={`font-semibold text-base ${item.closed ? 'text-red-500' : 'text-slate-600 font-mono'}`}>
                    {item.hours}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-[#2a4692] text-white py-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,white_10%,transparent_70%)] pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center space-y-6 relative z-10">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Vil du vide, hvor meget din lejeaftale er værd?
          </h2>
          <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
            Få en gratis og uforpligtende vurdering af din telemast i dag. Vi sidder klar til at hjælpe dig med professionel rådgivning.
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-4">
            <a
              href="tel:+4550358378"
              className="bg-transparent hover:bg-white/10 text-white border border-white/30 font-bold px-8 py-4 rounded-md transition-all inline-flex items-center gap-2 cursor-pointer"
            >
              Ring 50 358 378
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
